import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, MapPin, Users, ArrowRight } from "@phosphor-icons/react";
import { offerStartsAt, offerCurrentPrice, offerOriginalPrice, offerDiscountPct } from "../lib/supabase";

const formatTime = (d) => {
  if (!d) return "—";
  return d.toLocaleString("es-ES", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const minutesUntil = (d) => {
  if (!d) return null;
  const ms = d.getTime() - Date.now();
  if (ms <= 0) return null;
  const h = Math.floor(ms / 3.6e6);
  const m = Math.floor((ms % 3.6e6) / 60000);
  if (h >= 24) return `en ${Math.floor(h/24)}d`;
  if (h > 0) return `en ${h}h ${m}m`;
  return `en ${m}m`;
};

const ActivityCard = ({ offer, index = 0 }) => {
  const business = offer.business || offer.businesses || {};
  const starts = offerStartsAt(offer);
  const currentPrice = offerCurrentPrice(offer);
  const originalPrice = offerOriginalPrice(offer);
  const discountPct = offerDiscountPct(offer);
  const eta = minutesUntil(starts);
  const category = business.category;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: Math.min(index * 0.05, 0.4), ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -3 }}
      className="group relative bg-white rounded-2xl border border-forest/8 hover:border-sage/50 transition-colors overflow-hidden shadow-[0_1px_2px_rgba(44,62,45,0.04)] hover:shadow-[0_12px_32px_-12px_rgba(44,62,45,0.18)]"
      data-testid={`offer-card-${offer.id}`}
    >
      <Link to={`/actividad/${offer.id}`} className="block">
        <div className="relative aspect-[4/3] overflow-hidden bg-mist rounded-t-2xl">
          {(offer.image_url || business.image_url) && (
            <img
              src={offer.image_url || business.image_url}
              alt={offer.title}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />

          <div className="absolute top-3 left-3 z-20 flex flex-col gap-2">
            {discountPct > 0 && (
              <span className="bg-sage-deep text-paper px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-full">-{discountPct}%</span>
            )}
            {category && (
              <span className="bg-paper/90 backdrop-blur-sm text-forest px-2.5 py-1 text-[10px] uppercase tracking-wider rounded-full">{category}</span>
            )}
          </div>

          {eta && (
            <span className="absolute top-3 right-3 z-20 bg-paper/90 backdrop-blur-sm text-sage-deep px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-full">{eta}</span>
          )}
        </div>

        <div className="p-5 space-y-3">
          <div>
            <h3 className="font-heading font-semibold text-lg leading-tight line-clamp-2 text-forest group-hover:text-sage-deep transition">{offer.title}</h3>
            <p className="text-xs text-stone mt-1 truncate">{business.name}</p>
          </div>

          <div className="flex items-center gap-3 text-xs text-stone">
            <span className="flex items-center gap-1"><Clock size={12} />{formatTime(starts)}</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-stone">
            {business.address && <span className="flex items-center gap-1"><MapPin size={12} />{business.address.split(",")[0]}</span>}
            <span className="flex items-center gap-1"><Users size={12} />{offer.available_slots} libres</span>
          </div>

          <div className="flex items-end justify-between pt-3 border-t border-forest/8">
            <div>
              {discountPct > 0 && (
                <div className="text-[10px] uppercase tracking-wider text-ash line-through">{originalPrice.toFixed(2)}€</div>
              )}
              <div className="font-heading text-2xl font-semibold text-sage-deep leading-none">{currentPrice.toFixed(2)}€</div>
            </div>
            <div className="flex items-center gap-1 text-xs uppercase tracking-wider font-medium text-stone group-hover:text-sage-deep transition">
              Reservar <ArrowRight size={14} />
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ActivityCard;
